import { useNavigate } from 'react-router-dom';
import DOMPurify from 'dompurify';
import type { ChatMessage as ChatMessageType } from '../../store/chatStore';
import { useState } from 'react';

interface Props {
  message: ChatMessageType;
}

/** Echappe les caracteres HTML dangereux */
function escapeHtml(str: string): string {
  return str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

/** Rend le contenu markdown simplifie (gras, italique, code, liens entites). */
function renderMarkdown(
  content: string,
  entityMentions?: ChatMessageType['entityMentions'],
  onEntityClick?: (id: string) => void
) {
  // Remplacer les blocs de code ```...```
  const parts: (string | JSX.Element)[] = [];
  const codeBlockRegex = /```(\w*)\n?([\s\S]*?)```/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  const withInline = (text: string, key: string) => {
    // Gras **text**
    let processed = text.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
    // Italique *text*
    processed = processed.replace(/(?<!\*)\*(?!\*)(.*?)(?<!\*)\*(?!\*)/g, '<em>$1</em>');
    // Code inline `text`
    processed = processed.replace(
      /`([^`]+)`/g,
      '<code class="bg-surface-tertiary px-1 py-0.5 rounded text-cyber-cyan text-sm">$1</code>'
    );

    // Remplacer les mentions d'entites (noms echappes pour eviter XSS)
    if (entityMentions) {
      for (const entity of entityMentions) {
        const escaped = entity.name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const safeName = escapeHtml(entity.name);
        const safeId = escapeHtml(entity.id);
        processed = processed.replace(
          new RegExp(escaped, 'gi'),
          `<button data-entity-id="${safeId}" class="text-cyber-cyan underline hover:text-cyan-300 cursor-pointer">${safeName}</button>`
        );
      }
    }

    return (
      <span
        key={key}
        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(processed) }}
        onClick={(e) => {
          const target = e.target as HTMLElement;
          if (target.dataset.entityId && onEntityClick) {
            onEntityClick(target.dataset.entityId);
          }
        }}
      />
    );
  };

  while ((match = codeBlockRegex.exec(content)) !== null) {
    if (match.index > lastIndex) {
      parts.push(withInline(content.slice(lastIndex, match.index), `text-${lastIndex}`));
    }
    parts.push(
      <pre
        key={`code-${match.index}`}
        className="bg-surface-tertiary rounded-lg p-3 my-2 overflow-x-auto text-sm text-gray-300 border border-border-subtle"
      >
        <code>{match[2]}</code>
      </pre>
    );
    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < content.length) {
    parts.push(withInline(content.slice(lastIndex), `text-${lastIndex}`));
  }

  return parts;
}

export default function ChatMessage({ message }: Props) {
  const navigate = useNavigate();
  const [copied, setCopied] = useState(false);
  const isUser = message.role === 'user';

  const handleCopy = async () => {
    await navigator.clipboard.writeText(message.content);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleEntityClick = (entityId: string) => {
    navigate(`/entities?id=${entityId}`);
  };

  const time = new Date(message.timestamp).toLocaleTimeString('fr-FR', {
    hour: '2-digit',
    minute: '2-digit',
  });

  return (
    <div className={`flex gap-3 ${isUser ? 'flex-row-reverse' : ''} group`}>
      {/* Avatar */}
      <div
        className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
          isUser
            ? 'bg-blue-600'
            : 'bg-gradient-to-br from-cyber-cyan to-helios-600'
        }`}
      >
        {isUser ? (
          <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z" />
          </svg>
        ) : (
          <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 3v2.25m6.364.386l-1.591 1.591M21 12h-2.25m-.386 6.364l-1.591-1.591M12 18.75V21m-4.773-4.227l-1.591 1.591M5.25 12H3m4.227-4.773L5.636 5.636M15.75 12a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0z" />
          </svg>
        )}
      </div>

      {/* Bulle de message */}
      <div className={`max-w-[80%] ${isUser ? 'items-end' : 'items-start'}`}>
        <div
          className={`rounded-2xl px-4 py-3 text-sm leading-relaxed ${
            isUser
              ? 'bg-blue-700 text-white rounded-tr-sm'
              : 'bg-surface-tertiary text-gray-200 rounded-tl-sm border border-border-subtle'
          }`}
        >
          <div className="whitespace-pre-wrap">
            {renderMarkdown(message.content, message.entityMentions, handleEntityClick)}
          </div>
        </div>

        {/* Meta: timestamp + copy */}
        <div
          className={`flex items-center gap-2 mt-1 px-1 ${
            isUser ? 'justify-end' : 'justify-start'
          }`}
        >
          <span className="text-xs text-gray-500">{time}</span>
          {!isUser && (
            <button
              onClick={handleCopy}
              className="opacity-0 group-hover:opacity-100 text-gray-500 hover:text-gray-300 transition-opacity"
              title="Copier"
            >
              {copied ? (
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                </svg>
              ) : (
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15.666 3.888A2.25 2.25 0 0013.5 2.25h-3c-1.03 0-1.9.693-2.166 1.638m7.332 0c.055.194.084.4.084.612v0a.75.75 0 01-.75.75H9.75a.75.75 0 01-.75-.75v0c0-.212.03-.418.084-.612m7.332 0c.646.049 1.288.11 1.927.184 1.1.128 1.907 1.077 1.907 2.185V19.5a2.25 2.25 0 01-2.25 2.25H6.75A2.25 2.25 0 014.5 19.5V6.257c0-1.108.806-2.057 1.907-2.185a48.208 48.208 0 011.927-.184" />
                </svg>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
